import { deleteObject, getDownloadURL, ref, uploadBytes, uploadString, UploadMetadata } from 'firebase/storage';
import { storage } from './firebase';
import { logGlobalError } from 'shared/utils/globalErrorLogger';

/**
 * Generic Firebase Storage capabilities. Domain-specific file workflows
 * belong with their domain; these helpers only know bucket paths and URLs.
 *
 * The error contract matches the existing port: uploads return an empty URL
 * on failure and deletes resolve after logging the failure.
 *
 * Failures additionally go to Cloud Logging, not just the console. These calls
 * swallow their errors, so a `console.error` left the caller (and us) with no
 * record of WHY an upload failed — an expert whose resume upload failed during
 * onboarding produced no server-side trace at all, making the reports
 * un-diagnosable. `storageErrorCode` is the field worth reading: Firebase
 * Storage errors carry a `code` like `storage/unauthorized` (rules rejection)
 * or `storage/retry-limit-exceeded` (network stall), which names the cause.
 */
function storageErrorCode(err: unknown): string | undefined {
  if (typeof err === 'object' && err !== null && 'code' in err) {
    const code = (err as { code: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return undefined;
}

function logStorageError(context: string, err: unknown, metadata?: Record<string, unknown>): void {
  console.error('[storage] %s:', context, err);
  logGlobalError(err, {
    context: `storage.${context}`,
    severity: 'ERROR',
    metadata: { storageErrorCode: storageErrorCode(err), ...metadata },
  });
}

export async function uploadFile(
  path: string,
  data: Blob | Uint8Array | ArrayBuffer,
  metadata?: UploadMetadata,
): Promise<string> {
  const fileRef = ref(storage, path);
  const byteLength =
    data instanceof Uint8Array ? data.byteLength : 'byteLength' in data ? data.byteLength : data.size;
  let uploadedRef;
  try {
    uploadedRef = (await uploadBytes(fileRef, data, metadata)).ref;
  } catch (err) {
    logStorageError('uploadFile(' + path + ')', err, { path, byteLength });
    return '';
  }
  try {
    return await getDownloadURL(uploadedRef);
  } catch (err) {
    logStorageError('uploadFile(' + path + ') getDownloadURL', err, { path, byteLength });
    return '';
  }
}

export async function uploadDataUrl(path: string, dataUrl: string): Promise<string> {
  const fileRef = ref(storage, path);
  let uploadedRef;
  try {
    uploadedRef = (await uploadString(fileRef, dataUrl, 'data_url')).ref;
  } catch (err) {
    logStorageError('uploadDataUrl(' + path + ')', err);
    return '';
  }
  try {
    return await getDownloadURL(uploadedRef);
  } catch (err) {
    logStorageError('uploadDataUrl(' + path + ') getDownloadURL', err);
    return '';
  }
}

export async function getFileUrl(path: string): Promise<string> {
  try {
    return await getDownloadURL(ref(storage, path));
  } catch (err) {
    logStorageError('getFileUrl(' + path + ')', err);
    return '';
  }
}

export async function deleteFile(path: string): Promise<void> {
  try {
    await deleteObject(ref(storage, path));
  } catch (err) {
    logStorageError('deleteFile(' + path + ')', err);
  }
}

export async function deleteFileByUrl(url: string): Promise<void> {
  try {
    await deleteObject(ref(storage, url));
  } catch (err) {
    // The object is already gone, which is the outcome the caller wanted —
    // report it as a success rather than an error.
    if (storageErrorCode(err) === 'storage/object-not-found') return;
    logStorageError('deleteFileByUrl(' + url + ')', err);
  }
}
